import { Link, Navigate, useLocation } from 'react-router-dom';

import { AppShell } from '@/components/layout/AppShell';
import {
  Badge,
  ButtonLink,
  Card,
  EmptyState,
  ErrorState,
  Skeleton,
  SkeletonGroup,
} from '@/components/ui';
import { useSession } from '@/features/auth/sessionContext';
import { useMyOrders } from '@/lib/api/checkout';
import { formatDateTime, formatPaise } from '@/lib/format';
import type { OrderSummary } from '@/lib/api/types';
import { paths } from '@/routes/paths';

/**
 * /orders — everything this customer has bought.
 *
 * The counterpart to the tracking page: that one answers "where is *this*
 * order", reached from a link or the payment return, and this one answers "what
 * have I ordered", reached by someone who just wants to check. Without it the
 * only way back to an order was to still have its number.
 */
export function MyOrdersPage() {
  const session = useSession();
  const location = useLocation();
  const signedIn = session.status === 'authenticated';
  const { data, isLoading, isError, error, refetch, isFetching } = useMyOrders(signedIn);

  if (session.status === 'loading') {
    return (
      <AppShell title="Your orders">
        <SkeletonGroup label="Loading your orders">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="mt-3 h-24 w-full" />
        </SkeletonGroup>
      </AppShell>
    );
  }

  if (!signedIn) {
    // `from` so signing in lands them back here rather than on the home page.
    return <Navigate to={paths.signIn} replace state={{ from: location.pathname }} />;
  }

  if (isLoading) {
    return (
      <AppShell title="Your orders">
        <SkeletonGroup label="Loading your orders">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="mt-3 h-24 w-full" />
        </SkeletonGroup>
      </AppShell>
    );
  }

  if (isError || !data) {
    return (
      <AppShell title="Your orders">
        <ErrorState
          error={error}
          title="We could not load your orders"
          onRetry={() => void refetch()}
          retrying={isFetching}
        />
      </AppShell>
    );
  }

  if (data.items.length === 0) {
    return (
      <AppShell title="Your orders">
        <EmptyState
          title="Nothing here yet"
          description="Once you place an order it will show up here with its progress."
          action={<ButtonLink to={paths.order.upload}>Print something</ButtonLink>}
        />
      </AppShell>
    );
  }

  return (
    <AppShell
      title="Your orders"
      subtitle={`${data.total} order${data.total === 1 ? '' : 's'}`}
      action={<ButtonLink to={paths.order.upload}>Print something else</ButtonLink>}
    >
      {/* Two across on a wide screen. A list of order cards in a 32rem column
          on a 1440px monitor is mostly empty space, and the whole point of this
          page is scanning several orders at once. */}
      <ul className="flex flex-col gap-3 lg:grid lg:grid-cols-2 lg:gap-4">
        {data.items.map((order) => (
          <li key={order.id}>
            <OrderCard order={order} />
          </li>
        ))}
      </ul>
    </AppShell>
  );
}

function OrderCard({ order }: { order: OrderSummary }) {
  return (
    <Card>
      <Link
        to={`/orders/${order.order_no}`}
        className="-m-4 block rounded-card p-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus"
      >
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <span className="font-semibold">{order.order_no}</span>
          <Badge tone={tone(order.status)} srPrefix="Order status:">
            {order.status_label}
          </Badge>
        </div>

        <p className="mt-1 text-sm text-muted">
          {order.filename ?? 'Your document'} · {order.page_count} page
          {order.page_count === 1 ? '' : 's'} × {order.copies} cop
          {order.copies === 1 ? 'y' : 'ies'}
        </p>

        <div className="mt-2 flex flex-wrap items-baseline justify-between gap-2 text-sm">
          <span className="text-muted">{formatDateTime(order.created_at)}</span>
          <span className="font-semibold">{formatPaise(order.total_paise)}</span>
        </div>
      </Link>
    </Card>
  );
}

function tone(status: string): 'success' | 'warning' | 'danger' | 'neutral' {
  if (status === 'delivered') return 'success';
  if (status === 'cancelled' || status === 'payment_failed') return 'danger';
  if (status === 'awaiting_payment') return 'warning';
  return 'neutral';
}
